import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  BsBuildings,
  BsPersonVcard,
  BsSignpostSplit,
  BsImages,
  BsCheckCircleFill,
  BsSquare,
  BsCheckSquareFill,
  BsFileEarmarkArrowUp,
  BsFileEarmarkText,
  BsX,
} from "react-icons/bs";
import "./ListerWizard.css";

import GeoApiAuto from "../GeoApiAuto/GeoApiAuto";
import useImageUploader from "../../hooks/useImageUpload";
import { createLeadId, saveProviderLeadEmail, submitProviderLead } from "../../actions/leadActions";

const STORAGE_TYPES = [
  { key: "self_storage", label: "Self Storage Units" },
  { key: "climate_controlled", label: "Climate Controlled" },
  { key: "vehicle", label: "Vehicle Storage" },
  { key: "business", label: "Business Storage" },
];

const UNIT_COUNT_OPTIONS = ["1 - 10", "11 - 50", "51 - 150", "150+"];

const STEPS = [
  { title: "How To List", icon: <BsSignpostSplit /> },
  { title: "Your Facility", icon: <BsBuildings /> },
  { title: "Your Details", icon: <BsPersonVcard /> },
  { title: "Finish Up", icon: <BsImages /> },
];

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ListerWizard = ({ utmParams }) => {
  const navigate = useNavigate();
  const cloudName = "dxqfv1xa2";
  const { handleImageUpload, uploading } = useImageUploader(cloudName);

  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);
  const [docUploading, setDocUploading] = useState(false);

  // One id for this trip through the wizard, shared by the Step 2 email capture
  // and the final submission so the two are recognisable as the same lead.
  const leadIdRef = useRef(createLeadId());
  // The address we have already persisted. Re-entering Step 2 or double-clicking
  // Continue with the same address must not send a second record.
  const savedEmailRef = useRef(null);
  const emailAttemptsRef = useRef(0);
  // The save currently in flight, as { email, promise }. `savingEmail` drives the
  // button, but state does not update synchronously, so clicks landing in the same
  // tick would all get past it — this ref does not. It holds the promise rather
  // than a boolean so a later caller can JOIN the request instead of being turned
  // away: clicking Continue blurs the email field first, and that blur may already
  // have started the very save the click is about to ask for.
  const inFlightEmailRef = useRef(null);
  const [formData, setFormData] = useState({
    storage_types: [],
    unit_count: "",
    location: null,
    full_name: "",
    business_name: "",
    email: "",
    phone: "",
    listing_preference: "",
    notes: "",
    photos: [],
    documents: [],
  });

  const changeInputHandler = (event) => {
    setFormData((prev) => ({ ...prev, [event.target.id]: event.target.value }));
  };

  const toggleStorageType = (key) => {
    setFormData((prev) => ({
      ...prev,
      storage_types: prev.storage_types.includes(key)
        ? prev.storage_types.filter((t) => t !== key)
        : [...prev.storage_types, key],
    }));
  };

  const getLocationData = (value) => {
    if (!value) return;
    const { formatted, city, state, postcode, lat, lon } = value.properties;
    setFormData((prev) => ({
      ...prev,
      location: { formatted, city, state, postcode, lat, lon },
    }));
  };

  const validateFacilityStep = () => {
    if (!EMAIL_REGEX.test(formData.email)) {
      toast.error("Please enter a valid email address");
      return false;
    }
    if (formData.storage_types.length === 0) {
      toast.error("Please select at least one storage type");
      return false;
    }
    if (!formData.unit_count) {
      toast.error("Please tell us roughly how many units you have");
      return false;
    }
    if (!formData.location) {
      toast.error("Please select your facility location from the suggestions");
      return false;
    }
    return true;
  };

  const validateContactStep = () => {
    if (!formData.full_name.trim()) {
      toast.error("Please enter your name");
      return false;
    }
    if (!formData.phone.trim()) {
      toast.error("Please enter your phone number");
      return false;
    }
    return true;
  };

  // Sends the address to the lead sheet, at most once per address. Resolves true
  // when it is recorded, false when the request failed. Both the blur handler and
  // Continue go through here, so the lead_id and duplicate protection are shared.
  const captureEmail = async () => {
    const email = formData.email;

    // Already persisted this exact address in this session.
    if (savedEmailRef.current === email) return true;

    const inFlight = inFlightEmailRef.current;
    if (inFlight) {
      // Same address already on the wire — join it rather than sending a second.
      if (inFlight.email === email) return inFlight.promise;
      // A different address is mid-flight; let it settle before replacing it.
      await inFlight.promise;
      if (savedEmailRef.current === email) return true;
    }

    setSavingEmail(true);
    const promise = saveProviderLeadEmail({
      email: email,
      lead_id: leadIdRef.current,
      utm: utmParams,
    })
      .then(() => {
        savedEmailRef.current = email;
        emailAttemptsRef.current = 0;
        return true;
      })
      .catch(() => false)
      .finally(() => {
        inFlightEmailRef.current = null;
        setSavingEmail(false);
      });

    // Assigned synchronously, before any await, so same-tick callers see it.
    inFlightEmailRef.current = { email: email, promise: promise };
    return promise;
  };

  // Validating on blur gets the address to the lead sheet as soon as the user
  // moves to the next field, instead of waiting for Continue.
  const emailBlurHandler = async () => {
    // Nothing typed yet, or the field was cleared — say nothing and let Continue
    // do the complaining. Scolding someone for tabbing through an empty field is
    // not worth a toast.
    if (!formData.email) return;

    if (!EMAIL_REGEX.test(formData.email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    // A failure here stays quiet: the user has not asked to move on yet, and
    // Continue still validates and reports before letting them past.
    await captureEmail();
  };

  // Continue remains the safety net: it re-validates and, if the blur save never
  // happened or failed, sends the address itself before moving on.
  const nextFromFacility = async () => {
    if (!validateFacilityStep()) return;

    if (await captureEmail()) {
      setStep(2);
      return;
    }

    emailAttemptsRef.current += 1;
    if (emailAttemptsRef.current >= 2) {
      // Retried and still failing. The address is safe in formData, in the
      // localStorage mirror and in the final submission, so hold the lead
      // rather than the person — a dead sheet should not end the funnel.
      savedEmailRef.current = formData.email;
      emailAttemptsRef.current = 0;
      toast.warn("We couldn't confirm your email was saved — we'll send it again at the end.");
      setStep(2);
      return;
    }
    toast.error("We couldn't save your email. Please check your connection and try again.");
  };

  const nextFromContact = () => {
    if (validateContactStep()) setStep(3);
  };

  // Self-serve: straight to account creation (fork is the first step, so no
  // details have been collected yet) — still record the click for tracking
  const selfServeHandler = () => {
    submitProviderLead({ ...formData, listing_preference: "self_serve", utm: utmParams }).catch(
      () => {}
    );
    toast.success("Great! Create your account to continue");
    navigate("/sign-up-storage-provider");
  };

  const conciergeHandler = () => {
    setFormData((prev) => ({ ...prev, listing_preference: "concierge" }));
    setStep(1);
  };

  const photoUploadHandler = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    const urls = await handleImageUpload(files);
    if (urls && urls.length > 0) {
      setFormData((prev) => ({ ...prev, photos: [...prev.photos, ...urls] }));
      toast.success(`${urls.length} photo(s) uploaded`);
    }
    // reset so the same file can be re-picked after a remove
    event.target.value = "";
  };

  const removePhoto = (url) => {
    setFormData((prev) => ({ ...prev, photos: prev.photos.filter((p) => p !== url) }));
  };

  // Docs (PDF / Excel / Word / CSV) go to Cloudinary's auto endpoint — the image
  // endpoint rejects non-image files.
  const documentUploadHandler = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    try {
      setDocUploading(true);
      const uploaded = [];
      for (const file of Array.from(files)) {
        const body = new FormData();
        body.append("file", file);
        body.append("upload_preset", "gmrltpup");
        const response = await fetch(`https://api.cloudinary.com/v1_1/${cloudName}/auto/upload`, {
          method: "POST",
          body,
        });
        if (response.ok) {
          const data = await response.json();
          uploaded.push({ name: file.name, url: data.secure_url });
        } else {
          toast.error(`Could not upload ${file.name}`);
        }
      }
      if (uploaded.length > 0) {
        setFormData((prev) => ({ ...prev, documents: [...prev.documents, ...uploaded] }));
        toast.success(`${uploaded.length} document(s) attached`);
      }
    } catch (error) {
      toast.error("Issue while uploading documents");
    } finally {
      setDocUploading(false);
      event.target.value = "";
    }
  };

  const removeDocument = (url) => {
    setFormData((prev) => ({ ...prev, documents: prev.documents.filter((d) => d.url !== url) }));
  };

  const submitLeadHandler = async () => {
    try {
      setSubmitting(true);
      await submitProviderLead({
        ...formData,
        lead_id: leadIdRef.current,
        stage: "complete",
        listing_preference: "concierge",
        utm: utmParams,
      });
      setStep(4);
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const progress = Math.round(((step + 1) / (STEPS.length + 1)) * 100);

  return (
    <div className="card shadow border-0 mx-auto" style={{ maxWidth: "720px", borderRadius: "20px" }}>
      <div className="card-body p-4 p-md-5">
        {step < 4 && (
          <>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className="fw-semibold blueTextColr">
                {STEPS[step].icon} <span className="ms-1">{STEPS[step].title}</span>
              </span>
              <span className="text-muted small">
                Step {step + 1} of {STEPS.length}
              </span>
            </div>
            <div className="progress mb-4" style={{ height: "8px" }}>
              <div
                className="progress-bar"
                role="progressbar"
                style={{ width: `${progress}%`, backgroundColor: "#FFCC00" }}
                aria-valuenow={progress}
                aria-valuemin="0"
                aria-valuemax="100"
              ></div>
            </div>
          </>
        )}

        {/* STEP 2 — FACILITY (concierge path) */}
        {step === 1 && (
          <div>
            <h3 className="blueTextColr fw-bold mb-1">Tell us about your space</h3>
            <p className="text-muted mb-4">Takes less than 2 minutes — no account needed.</p>

            <div className="mb-4">
              <label htmlFor="email" className="form-label fw-semibold">
                Email address
              </label>
              <input
                type="email"
                id="email"
                className="form-control"
                placeholder="you@example.com"
                value={formData.email}
                onChange={changeInputHandler}
                onBlur={emailBlurHandler}
              />
            </div>

            <label className="form-label fw-semibold">
              What type of storage do you offer?{" "}
              <span className="text-muted fw-normal">(select all that apply)</span>
            </label>
            <div className="row g-2 mb-4">
              {STORAGE_TYPES.map((type) => {
                const isSelected = formData.storage_types.includes(type.key);
                return (
                  <div className="col-sm-6" key={type.key}>
                    <button
                      type="button"
                      role="checkbox"
                      aria-checked={isSelected}
                      className={`storage-type-option ${isSelected ? "selected" : ""}`}
                      onClick={() => toggleStorageType(type.key)}
                    >
                      {isSelected ? (
                        <BsCheckSquareFill className="stc-icon" />
                      ) : (
                        <BsSquare className="stc-icon" />
                      )}
                      {type.label}
                    </button>
                  </div>
                );
              })}
            </div>

            <div className="mb-4">
              <label htmlFor="unit_count" className="form-label fw-semibold">
                Roughly how many units?
              </label>
              <select
                id="unit_count"
                className="form-select"
                value={formData.unit_count}
                onChange={changeInputHandler}
              >
                <option value="">Select a range</option>
                {UNIT_COUNT_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-4">
              <label className="form-label fw-semibold">Where is your facility?</label>
              <div className="wizard-geo">
                <GeoApiAuto getLocationData={getLocationData} />
              </div>
              {formData.location && (
                <small className="text-success d-block mt-1">
                  <BsCheckCircleFill className="me-1" />
                  {formData.location.formatted}
                </small>
              )}
            </div>

            <div className="d-flex gap-2">
              <button className="wizard-btn-secondary px-4" onClick={() => setStep(0)}>
                Back
              </button>
              {/* Not disabled while saving: clicking Continue blurs the email
                  field, and that blur can start the save a moment before the
                  click lands — a disabled button would swallow the click and
                  leave the user stuck. Sending twice is already impossible,
                  because captureEmail joins the request in flight. */}
              <button className="wizard-btn-primary flex-grow-1" onClick={nextFromFacility}>
                {savingEmail ? "Saving…" : "Continue"}
              </button>
            </div>
          </div>
        )}

        {/* STEP 3 — CONTACT (concierge path) */}
        {step === 2 && (
          <div>
            <h3 className="blueTextColr fw-bold mb-1">How can we reach you?</h3>
            <p className="text-muted mb-4">We'll use this to set up your listing — no spam, ever.</p>

            <div className="mb-3">
              <label htmlFor="full_name" className="form-label fw-semibold">
                Your name
              </label>
              <input
                type="text"
                id="full_name"
                className="form-control"
                placeholder="e.g. Jordan Smith"
                value={formData.full_name}
                onChange={changeInputHandler}
              />
            </div>
            <div className="mb-3">
              <label htmlFor="business_name" className="form-label fw-semibold">
                Business name <span className="text-muted fw-normal">(optional)</span>
              </label>
              <input
                type="text"
                id="business_name"
                className="form-control"
                placeholder="e.g. Smith Storage LLC"
                value={formData.business_name}
                onChange={changeInputHandler}
              />
            </div>
            <div className="mb-4">
              <label htmlFor="phone" className="form-label fw-semibold">
                Phone number
              </label>
              <input
                type="tel"
                id="phone"
                className="form-control"
                placeholder="+15551234567"
                value={formData.phone}
                onChange={changeInputHandler}
              />
            </div>

            <div className="d-flex gap-2">
              <button className="wizard-btn-secondary px-4" onClick={() => setStep(1)}>
                Back
              </button>
              <button className="wizard-btn-primary flex-grow-1" onClick={nextFromContact}>
                Continue
              </button>
            </div>
          </div>
        )}

        {/* STEP 1 — FORK */}
        {step === 0 && (
          <div>
            <h3 className="blueTextColr fw-bold mb-1">How would you like to get listed?</h3>
            <p className="text-muted mb-4">Either way, listing is free.</p>

            <div className="row g-3 mb-4">
              <div className="col-md-6">
                <div
                  className="card h-100 border-2"
                  style={{ borderColor: "#FFCC00", borderRadius: "15px", cursor: "pointer" }}
                  onClick={conciergeHandler}
                >
                  <div className="card-body text-center p-4">
                    <span className="badge bg-orange text-dark mb-2">Most popular</span>
                    <h5 className="fw-bold blueTextColr">List it for me</h5>
                    <p className="text-muted small mb-3">
                      Share a few details and photos — our team builds your listing and contacts you
                      within 24 hours. Zero effort.
                    </p>
                    <button className="wizard-btn-primary w-100">Let us handle it</button>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div
                  className="card h-100"
                  style={{ borderRadius: "15px", cursor: "pointer" }}
                  onClick={selfServeHandler}
                >
                  <div className="card-body text-center p-4">
                    <span className="badge bg-light text-dark mb-2">Full control</span>
                    <h5 className="fw-bold blueTextColr">I'll manage it myself</h5>
                    <p className="text-muted small mb-3">
                      Create your account and use our dashboard to add facilities, set prices, and
                      manage bookings yourself.
                    </p>
                    <button className="wizard-btn-secondary w-100">Create my account</button>
                  </div>
                </div>
              </div>
            </div>

          </div>
        )}

        {/* STEP 4 — CONCIERGE DETAILS (notes, photos, documents) */}
        {step === 3 && (
          <div>
            <h3 className="blueTextColr fw-bold mb-1">Almost done!</h3>
            <p className="text-muted mb-4">
              Anything extra helps us build a better listing — all of this is optional.
            </p>

            <div className="mb-3">
              <label htmlFor="notes" className="form-label fw-semibold">
                Tell us about your facility
              </label>
              <textarea
                id="notes"
                className="form-control"
                rows="4"
                placeholder="Gate hours, security cameras, unit sizes, current pricing — anything you'd like renters to know"
                value={formData.notes}
                onChange={changeInputHandler}
              ></textarea>
            </div>

            <div className="mb-4">
              <label className="form-label fw-semibold mb-1">Photos of your facility</label>
              <p className="text-muted small mb-2">
                Select several at once (hold Ctrl / Cmd), or keep clicking to add more.
              </p>
              <input
                type="file"
                id="lead_photos"
                accept="image/*"
                multiple
                hidden
                onChange={photoUploadHandler}
                disabled={uploading}
              />
              <label htmlFor="lead_photos" className={`upload-tile ${uploading ? "disabled" : ""}`}>
                <BsImages size={20} />
                {uploading ? "Uploading photos…" : "Add photos"}
              </label>
              {formData.photos.length > 0 && (
                <div className="d-flex flex-wrap gap-2 mt-2">
                  {formData.photos.map((url) => (
                    <div key={url} className="photo-thumb">
                      <img src={url} alt="Facility" />
                      <button
                        type="button"
                        className="photo-thumb-remove"
                        aria-label="Remove photo"
                        onClick={() => removePhoto(url)}
                      >
                        <BsX size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="mb-4">
              <label className="form-label fw-semibold mb-1">Unit details document</label>
              <p className="text-muted small mb-2">
                Already have your unit list in a file? Attach a PDF, Excel, Word, or CSV.
              </p>
              <input
                type="file"
                id="lead_docs"
                accept=".pdf,.xls,.xlsx,.csv,.doc,.docx"
                multiple
                hidden
                onChange={documentUploadHandler}
                disabled={docUploading}
              />
              <label htmlFor="lead_docs" className={`upload-tile ${docUploading ? "disabled" : ""}`}>
                <BsFileEarmarkArrowUp size={20} />
                {docUploading ? "Uploading documents…" : "Attach documents"}
              </label>
              {formData.documents.length > 0 && (
                <div className="d-flex flex-column gap-2 mt-2">
                  {formData.documents.map((doc) => (
                    <div key={doc.url} className="doc-chip">
                      <BsFileEarmarkText size={18} />
                      <span className="doc-chip-name">{doc.name}</span>
                      <button
                        type="button"
                        className="doc-chip-remove"
                        aria-label={`Remove ${doc.name}`}
                        onClick={() => removeDocument(doc.url)}
                      >
                        <BsX size={18} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="d-flex gap-2">
              <button className="wizard-btn-secondary px-4" onClick={() => setStep(2)}>
                Back
              </button>
              <button
                className="wizard-btn-primary flex-grow-1"
                onClick={submitLeadHandler}
                disabled={submitting || uploading || docUploading}
              >
                {submitting ? "Submitting…" : "Submit — list my storage"}
              </button>
            </div>
          </div>
        )}

        {/* STEP 5 — THANK YOU */}
        {step === 4 && (
          <div className="text-center py-4">
            <BsCheckCircleFill className="text-success mb-3" style={{ fontSize: "4rem" }} />
            <h3 className="blueTextColr fw-bold">You're all set, {formData.full_name.split(" ")[0]}!</h3>
            <p className="text-muted mx-auto" style={{ maxWidth: "420px" }}>
              Our team will build your listing and contact you at <strong>{formData.email}</strong>{" "}
              within 24 hours. Keep an eye on your inbox!
            </p>
            <button className="wizard-btn-secondary mt-2" onClick={() => navigate("/")}>
              Back to home
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ListerWizard;
